import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';

import { SkuQuantityType } from 'ish-core/models/product/product.model';
import { getServerConfigParameter } from 'ish-core/store/core/server-config';
import { getCustomerAttributes } from 'ish-core/store/customer/user';
import { whenTruthy } from 'ish-core/utils/operators';

import { UpdateQuickOrderItem } from '../models/custom-quickorder/custom-quickorder.model';
import {
  addQuickOrderFlyOutItemsToBasket,
  addQuickOrderItemsToBasket,
  createQuickOrderFlyoutItems,
  createQuickOrderItems,
  createQuickOrderUploadItems,
  deleteQuickOrderItems,
  loadQuickOrder,
  updateQuickOrderItem,
} from '../store/quick-order';
import {
  getQuickOrder,
  getQuickOrderLoading,
  getQuickOrderValidations,
} from '../store/quick-order/quick-order.selectors';

/* eslint-disable @typescript-eslint/member-ordering */
@Injectable({ providedIn: 'root' })
export class CustomQuickorderFacade {
  constructor(private store: Store) {}

  quickOrder$ = this.store.pipe(select(getQuickOrder));
  quickOrderLoading$ = this.store.pipe(select(getQuickOrderLoading));
  quickOrderValidations$ = this.store.pipe(select(getQuickOrderValidations));
  quickOrderTotal$ = this.quickOrder$.pipe(
    whenTruthy(),
    select(quickOrder => quickOrder.quickOrderTotal)
  );
  customerFreeFreightThresholdAttribute$ = this.store.pipe(
    select(getCustomerAttributes),
    whenTruthy(),
    map(attributes => {
      const freeFreightThresholdAttr = attributes.find(attribute => attribute.name === 'FreeFreightThreshold');
      const freeFreightThreshold = Number(freeFreightThresholdAttr?.value);
      return isNaN(freeFreightThreshold) ? undefined : freeFreightThreshold;
    })
  );
  customerFreeFreightThreshold$ = combineLatest([
    this.customerFreeFreightThresholdAttribute$,
    this.quickOrderTotal$,
  ]).pipe(
    whenTruthy(),
    map(([freeFreightThreshold, quickOrderTotal]) => {
      const remaining = freeFreightThreshold - quickOrderTotal.value;
      return remaining > 0 ? Number(remaining.toFixed(2)) : undefined;
    })
  );
  basketMaxItemSize$ = this.store.pipe(
    select(getServerConfigParameter<number>('basket.maxItemSize')),
    map(size => (typeof size === 'number' && size > 0 ? size : 100))
  );

  loadQuickOrder() {
    this.store.dispatch(loadQuickOrder());
  }

  createQuickOrderItems(quickOrderItems: UpdateQuickOrderItem[]) {
    this.store.dispatch(createQuickOrderItems({ quickOrderItems }));
  }
  createQuickOrderFlyoutItems(quickOrderItems: UpdateQuickOrderItem[]) {
    this.store.dispatch(createQuickOrderFlyoutItems({ quickOrderItems }));
  }
  createQuickOrderUploadItems(quickOrderItems: UpdateQuickOrderItem[]) {
    this.store.dispatch(createQuickOrderUploadItems({ quickOrderItems }));
  }

  updateQuickOrderItem(itemId: string, quickOrderItem: UpdateQuickOrderItem) {
    this.store.dispatch(updateQuickOrderItem({ itemId, quickOrderItem }));
  }

  deleteQuickOrderItems(itemIds: string[]) {
    this.store.dispatch(deleteQuickOrderItems({ itemIds }));
  }

  getQuickOrder$() {
    this.loadQuickOrder();
    return this.quickOrder$;
  }

  addQuickOrderToBasket(quickOrderItems: SkuQuantityType[]) {
    this.store.dispatch(addQuickOrderItemsToBasket({ items: quickOrderItems }));
  }

  addFlyOutQuickOrderToBasket(quickOrderItems: SkuQuantityType[]) {
    this.store.dispatch(addQuickOrderFlyOutItemsToBasket({ items: quickOrderItems }));
  }
}
